import * as React from "react";
import { Form, Select } from "antd";
import { ObjectO2 } from "src/pages/ManagementPage/Obj2Tab/Obj2Nodes";
import { ZOption } from "src/types/ZOption";
import { filterOptionByName } from "src/common/filterOptionByName";
import { loadObjectAttrinbutesAll } from "src/pages/ManagementPage/objectsApi";
import { onError } from "src/common/onError";
import { makeFieldOptions } from "src/common/makeFieldOptions";
import { DefaultOptionType } from "antd/es/select";
import { ZAttribute } from "src/types/ZAttribute";
import { classNames } from "src/common/classNames";
import styles from "./BsObjectSection.module.less";

/**
 * Общий компонент для форм настроек различных бизнес-сервисов
 * Позволяет выбирать объект и атрибуты для этого объекта
 *  Объект
 * ┌───────────────────────────────────────────┐
 * │ Объект                                    │
 * └───────────────────────────────────────────┘
 *  Дата начала           Дата окончания
 * ┌───────────────────┐ ┌─────────────────────┐
 * │ Дата начала       │ │ Дата окончания      │
 * └───────────────────┘ └─────────────────────┘
 *
 * Список объектов (objList) берётся из самой верхней ветки в дереве админки  (где показаны объекты, их атрибуты и прочее).
 * Это свойство treeData
 *
 * При изменении объекта:
 * - очищаются все принадлежащие ему поля с атрибутами
 * - для полей загружаются новые списки опций из атрибутов нового объекта
 * - выполняется автоподстановка (для обязательных полей).
 *
 * Автоподстановка использует доступные данные.
 * Например, если в списке только одна опция, то она сразу подставляется в поле, т.к. других вариантов нет.
 * Иначе выполняется поиск совпадений между лейблами поля и опции.
 * Например, поле называется "Дата начала" и в списке есть опция "Дата начала".
 */

type AttrDef = {
  label: string;
  attrName: string; // Имя поля в структуре данных, которое содержит attrId
  hidden?: boolean;
  attrValid?: (attr: ZAttribute) => boolean;
  onNewLine?: boolean;
  optional?: boolean; // Необязательное поле (большинство обязательные)
  CustomRender?: React.FC<PropsAttrSection>;
};
// CustomRender позволяет переделать выводимый контрол атрибута полностью или частично.
// Частичная переделка предполагает, что кастомный компонент модифицирует пропсы и вызывает AttrSection

type PropsBsObjectSection = {
  objName: string; // Имя поля в структуре данных, которое содержит objectId
  objLabel: string;
  objList: ObjectO2[];
  attrs?: AttrDef[];
};

export const BsObjectSection = (
  props: PropsBsObjectSection,
): React.ReactNode => {
  const { objName, objLabel, objList, attrs } = props;
  const objOptions: ZOption[] = React.useMemo(
    () =>
      objList.map(({ object }) => ({
        value: String(object.id),
        label: object.name,
      })),
    [objList],
  );
  const form = Form.useFormInstance();
  const curObjectId: string | undefined = Form.useWatch(objName);
  const curObject: ObjectO2 | undefined = React.useMemo(
    () => objList.find(({ object }) => object.id === curObjectId),
    [curObjectId],
  );
  const onObjChange = () => {
    attrs?.forEach(({ attrName }) => form.setFieldValue(attrName, null));
  };
  return (
    <>
      <Form.Item name={objName} label={objLabel} rules={[{ required: true }]}>
        <Select
          allowClear
          options={objOptions}
          showSearch
          filterOption={filterOptionByName}
          onChange={onObjChange}
          placeholder="Выбрать"
        />
      </Form.Item>
      {!!attrs?.length && (
        <div className={styles.attrBox}>
          {attrs
            .filter(({ hidden }) => !hidden)
            .map(({ CustomRender = AttrSection, ...attrProps }) => (
              <CustomRender
                key={attrProps.attrName}
                attrDef={attrProps}
                curObject={curObject}
              />
            ))}
        </div>
      )}
    </>
  );
};

export type PropsAttrSection = {
  attrDef: AttrDef;
  curObject: ObjectO2 | undefined;
};

export const AttrSection: React.FC<PropsAttrSection> = (props) => {
  const { attrDef, curObject } = props;
  const { attrName, label, attrValid, onNewLine, optional } = attrDef;
  const curObjectId = curObject?.object.id;
  const [attrOptions, setAttrOptions] = React.useState<DefaultOptionType[]>([]);
  const form = Form.useFormInstance();
  const load = async (): Promise<DefaultOptionType[]> => {
    if (!curObjectId) return [];
    // TODO: Тут можно было бы использовать какой-нибудь кеш, т.к. эти данные будут запрашивать сразу несколько полей.
    let list = await loadObjectAttrinbutesAll(curObjectId);
    if (attrValid) list = list.filter(attrValid);
    return makeFieldOptions(list);
  };
  // Попытка найти подходящее значение поля, используя имеющиеся данные.
  const autoComplete = (list: DefaultOptionType[]) => {
    let suitableOption: DefaultOptionType | undefined;
    if (list.length === 1) {
      // Если в списке один вариант, то подставляем его. Ведь всё равно других вариантов нет.
      [suitableOption] = list;
    } else {
      // Ищем вариант, где совпадает название поля и название атрибута
      suitableOption = list.find((opt) => opt.filterProp === label);
    }
    if (suitableOption) {
      form.setFieldValue(attrName, suitableOption.value);
    }
  };
  React.useEffect(() => {
    load()
      .then((list) => {
        setAttrOptions(list);
        if (!optional && !form.getFieldValue(attrName)) {
          // Автозаполнение выполняется для обязательных полей, если они не заполнены
          autoComplete(list);
        }
      })
      .catch(onError);
  }, [curObjectId]);
  return (
    <Form.Item
      key={attrName}
      label={label}
      name={attrName}
      rules={optional ? undefined : [{ required: true }]}
      className={classNames([[!!onNewLine, styles.onNewLine]])}
    >
      <Select
        allowClear
        options={attrOptions}
        showSearch
        placeholder="Выбрать"
        optionFilterProp="filterProp"
      />
    </Form.Item>
  );
};
